/**
 * Referral reach-first levers 1-3 (design gate rr-4eff4b570175, re-spec
 * evidence/2026-07-20-qlearn-growth-first-read/REFERRAL-REACH-RESPEC-V1.md).
 *
 * WHY this module exists rather than more branches inside the prompt component:
 * the measured diagnosis was reach, not conversion — 52 shows reached 7 users
 * because the score threshold filtered a ~118-user population down to ~nothing.
 * Levers 1-3 widen eligibility, widen placement, and convert repetition into
 * breadth. All three read from the EXISTING `referral-prompt` experiment payload
 * (schemaVersion-2), so each is independently flag-gated and dark-shippable.
 *
 * CONTROL PARITY WAS THE INVARIANT while the A/B ran: with no payload,
 * `resolveReachConfig` returns REACH_CONTROL, which reproduces the old
 * 70-score / 14-day / exam_result-only behavior exactly and touches no storage.
 *
 * THE A/B HAS ENDED (2026-10-20). Two arms that differed by 10 score points never
 * got enough traffic to read, and growth needed reach now. The prompt no longer
 * reads the experiment payload: every viewer gets INVITE_PROMPT_CONFIG below. The
 * payload helpers (resolveReachConfig, exposureSurfaces) stay because they hold
 * the gate-review fixes a future referral experiment should reuse.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const SHOW_LOG_KEY = 'referralPromptShowLog';
export const DISMISSAL_COUNT_KEY = 'referralPromptDismissals';

/**
 * Lifecycle bounds for the two persistent keys this module owns.
 *
 * REVIEW-SCOPE RULE (CLAUDE.md): client-side persistent state requires an
 * EXPLICIT lifecycle — TTL, clear-on-use, or single-use. The Jul-5 referrer-
 * reward leak was a naked setItem with no TTL that survived logout and leaked
 * one user's reward state onto the next. Both keys below store TIMESTAMPS, not
 * counters, so the count and its expiry are the same fact: entries outside the
 * window are dropped on every read AND every write, which bounds key size and
 * guarantees the state self-heals rather than accumulating forever.
 *
 * The dismissal hard stop is deliberately long-lived but NOT permanent — a user
 * who dismissed three times last quarter is not silenced for life.
 */
export const SHOW_LOG_WINDOW_DAYS = 7;
export const DISMISSAL_COUNT_TTL_DAYS = 180;

/**
 * The former control arm (the behavior before 2026-10-20), kept as the
 * fail-closed merge base for resolveReachConfig. Note the lever-3 caps are
 * `null` (feature absent) rather than a large sentinel number: a cap of
 * Number.MAX_SAFE_INTEGER would still make the gate read and write storage in
 * control, and control had to stay byte-identical AND storage-silent.
 * `subscriberSurfaces` is empty: in control, paying users saw the prompt nowhere.
 */
export const REACH_CONTROL = Object.freeze({
  minScoreToPrompt: 70,
  dismissalTtlDays: 14,
  enabledSurfaces: Object.freeze(['exam_result']),
  requireScore: true,
  maxShowsPerSurfacePerWeek: null,
  maxDismissalsBeforeStop: null,
  subscriberSurfaces: Object.freeze([]),
});

/**
 * The ONE config every viewer gets now that the `referral-prompt` A/B has ended.
 *
 * - Every finished quiz qualifies: no score gate. The score only picks the copy.
 * - Pro users are invited too (`subscriberSurfaces`), framed as a gift to the
 *   friend. Their own reward does not extend their plan: grantProDays adds a
 *   separate reward-plan grant that runs alongside the time they pay for (unless
 *   the active plan is the reward plan itself), so the prompt does not promise
 *   it to them. Reward lengths come from the server and are not set here.
 * - The paywall stays out of `enabledSurfaces`, so it still renders nothing, and
 *   it is not a subscriber surface, so turning it on later stays free-only.
 * - Frequency: a 3-day snooze after a dismissal, and at most 2 shows per surface
 *   in any rolling 7 days. After 3 dismissals on a surface it stops there until
 *   the oldest of them is DISMISSAL_COUNT_TTL_DAYS (180) old.
 */
export const INVITE_PROMPT_CONFIG = Object.freeze({
  minScoreToPrompt: 0, // unused while requireScore is false
  dismissalTtlDays: 3,
  enabledSurfaces: Object.freeze(['exam_result', 'diagnostic_report']),
  requireScore: false,
  maxShowsPerSurfacePerWeek: 2,
  maxDismissalsBeforeStop: 3,
  subscriberSurfaces: Object.freeze(['exam_result', 'diagnostic_report']),
});

/**
 * Merge an experiment payload over control. Unknown keys are ignored and a
 * missing/blank payload yields control untouched, so a malformed or partial
 * payload can only ever degrade toward the shipped behavior — never toward a
 * wider, more visible one.
 */
// Numeric payload fields fall back to CONTROL unless they are finite numbers.
// Gate-review finding: surfaces failed closed but numerics failed OPEN --
// `dismissalTtlDays: "abc"` produced NaN, and `now - dismissedAt < NaN` is false,
// so the snooze silently disappeared. A malformed payload must degrade toward the
// shipped behavior, never toward showing more.
const num = (value, fallback, { allowNull = false } = {}) => {
  if (allowNull && value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const resolveReachConfig = (payload) => {
  const p = payload && typeof payload === 'object' ? payload : {};
  return {
    minScoreToPrompt: num(p.minScoreToPrompt, REACH_CONTROL.minScoreToPrompt),
    dismissalTtlDays: num(p.dismissalTtlDays, REACH_CONTROL.dismissalTtlDays),
    enabledSurfaces: Array.isArray(p.enabledSurfaces)
      ? p.enabledSurfaces
      : REACH_CONTROL.enabledSurfaces,
    // Only an explicit boolean can turn the score gate off.
    requireScore: typeof p.requireScore === 'boolean'
      ? p.requireScore
      : REACH_CONTROL.requireScore,
    // null is meaningful here (cap absent), so it survives; garbage does not.
    maxShowsPerSurfacePerWeek: p.maxShowsPerSurfacePerWeek === undefined
      ? REACH_CONTROL.maxShowsPerSurfacePerWeek
      : num(p.maxShowsPerSurfacePerWeek, REACH_CONTROL.maxShowsPerSurfacePerWeek, { allowNull: true }),
    maxDismissalsBeforeStop: p.maxDismissalsBeforeStop === undefined
      ? REACH_CONTROL.maxDismissalsBeforeStop
      : num(p.maxDismissalsBeforeStop, REACH_CONTROL.maxDismissalsBeforeStop, { allowNull: true }),
    subscriberSurfaces: Array.isArray(p.subscriberSurfaces)
      ? p.subscriberSurfaces
      : REACH_CONTROL.subscriberSurfaces,
  };
};

/**
 * Surfaces on which EXPOSURE may be emitted — the union of `enabledSurfaces`
 * across EVERY variant of the experiment, deliberately INDEPENDENT of which
 * variant this user was assigned.
 *
 * WHY THE UNION, and why neither of the two remedies the gate offered:
 * exposure marks the PRE-TREATMENT point both arms share. Gating it on
 * `visible` biases the readout because the treatment moves visibility — that is
 * why the original code did not do so. But gating it on the USER'S OWN resolved
 * `enabledSurfaces` would bias it just as badly in the other direction: if the
 * treatment arm enables the paywall and control does not, then on the paywall
 * page only treatment users get enrolled, and the arms stop being comparable.
 *
 * The union is the only assignment-independent choice: on a page some arm can
 * serve, BOTH arms enrol identically; on a page no arm can serve, NEITHER does.
 * That restores the control-payload guarantee to exposure — mounting the
 * component on a new page enrols nobody until an arm actually serves there.
 */
export const exposureSurfaces = (experiment) => {
  const variants = experiment && Array.isArray(experiment.variants) ? experiment.variants : [];
  const union = new Set();
  variants.forEach((v) => {
    const surfaces = v && v.payload && Array.isArray(v.payload.enabledSurfaces)
      ? v.payload.enabledSurfaces
      : REACH_CONTROL.enabledSurfaces;
    surfaces.forEach((s) => union.add(s));
  });
  if (union.size === 0) REACH_CONTROL.enabledSurfaces.forEach((s) => union.add(s));
  return union;
};

/** Whether mounting on `surface` may enrol the viewer as exposed. */
export const isExposureSurface = (surface, experiment) => (
  Boolean(surface) && exposureSurfaces(experiment).has(surface)
);

// --- storage helpers -------------------------------------------------------
// Every read is defensive: absent storage (SSR, private mode, quota) and
// corrupt JSON both resolve to empty state rather than throwing, because a
// storage fault must not take down the results page the prompt renders on.

const readMap = (key) => {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeMap = (key, value) => {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable or full — the cap silently degrades to "no cap",
    // which is the shipped behavior. Never throw from a render path.
  }
};

const freshTimestamps = (list, now, windowDays) => {
  if (!Array.isArray(list)) return [];
  const cutoff = now - windowDays * DAY_MS;
  return list.filter((ts) => Number.isFinite(Number(ts)) && Number(ts) > cutoff);
};

/** Timestamps of prompt shows on `surface` still inside the rolling window. */
export const readShowLog = (surface, now = Date.now()) => (
  freshTimestamps(readMap(SHOW_LOG_KEY)[surface], now, SHOW_LOG_WINDOW_DAYS)
);

/** How many times the user dismissed on `surface` within the counter's TTL. */
export const readDismissalCount = (surface, now = Date.now()) => (
  freshTimestamps(readMap(DISMISSAL_COUNT_KEY)[surface], now, DISMISSAL_COUNT_TTL_DAYS).length
);

// Prune-then-append on write is what bounds the key: a user who sees the prompt
// weekly for a year still stores a handful of timestamps, not 52.
const appendPruned = (key, surface, now, windowDays) => {
  const map = readMap(key);
  map[surface] = [...freshTimestamps(map[surface], now, windowDays), now];
  writeMap(key, map);
};

export const recordShow = (surface, now = Date.now()) => {
  if (!surface) return;
  appendPruned(SHOW_LOG_KEY, surface, now, SHOW_LOG_WINDOW_DAYS);
};

export const recordDismissal = (surface, now = Date.now()) => {
  if (!surface) return;
  appendPruned(DISMISSAL_COUNT_KEY, surface, now, DISMISSAL_COUNT_TTL_DAYS);
};

// --- the gate --------------------------------------------------------------

/**
 * Whether the referral prompt may render on `surface` right now.
 *
 * Order matters: the cheap identity/eligibility refusals run before any storage
 * read, so a config with both caps null never touches localStorage.
 */
export const shouldShowOnSurface = ({
  surface,
  score,
  role,
  dismissedAt,
  subscriptionsLoaded,
  hasActivePlan,
  config = INVITE_PROMPT_CONFIG,
  now = Date.now(),
}) => {
  // Lever 2. An unrecognised or missing surface is refused, never defaulted
  // open — adding a placement must be an explicit payload act.
  if (!surface || !Array.isArray(config.enabledSurfaces)
      || !config.enabledSurfaces.includes(surface)) {
    return false;
  }

  // Staff never see promos.
  if (role === 'ROLE_ADMIN' || role === 'ROLE_TUTOR') return false;

  // On a subscriber surface the plan does not matter, so the gate neither waits
  // for it nor reads it. Everywhere else paying users are refused, including
  // while their plan is still loading, so a Pro user never gets a flash of it.
  const subscribersInvited = Array.isArray(config.subscriberSurfaces)
    && config.subscriberSurfaces.includes(surface);
  if (!subscribersInvited && (!subscriptionsLoaded || hasActivePlan)) return false;

  // Lever 1. With the score gate on, a finite score at or above the threshold is
  // required (the old control behavior). With it off (INVITE_PROMPT_CONFIG), any
  // score qualifies, and so do surfaces that carry no score at all.
  if (config.requireScore) {
    const numericScore = Number(score);
    if (!Number.isFinite(numericScore) || numericScore < config.minScoreToPrompt) {
      return false;
    }
  }

  if (dismissedAt && now - Number(dismissedAt) < config.dismissalTtlDays * DAY_MS) {
    return false;
  }

  // Lever 3. Repetition becomes breadth: a per-surface weekly cap plus a hard
  // stop after N dismissals on that surface. Both are per-surface on purpose —
  // a global cap would let one noisy placement silence the others.
  if (config.maxDismissalsBeforeStop != null
      && readDismissalCount(surface, now) >= config.maxDismissalsBeforeStop) {
    return false;
  }
  if (config.maxShowsPerSurfacePerWeek != null
      && readShowLog(surface, now).length >= config.maxShowsPerSurfacePerWeek) {
    return false;
  }

  return true;
};
